<template>
  <Dialog ref="modal" :parent-classes="['purchase-modal', { 'modal-lg': true }]" :rounded="false" init-on-mount>
    <div class="card p-2 rounded-0">
      <div class="card-body">
        

        <!-- PDF Preview component - always rendered for ref access -->
        <PreviewDocumentComponent ref="previewDocumentComponent" v-show="agreementData"/>

        <div v-if="loading">
          <div class="loader position-relative"></div>
          <div class="text-center">{{ loadingMessage }}</div>
          
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        

        <!-- Modal Footer -->
        <div class="card-footer border-top-0" v-show="!loading && agreementData">
          <button
              class="btn btn-link text-secondary text-decoration-none"
              @click="declineAgreement"
              :disabled="processing"
          >
            Decline
          </button>

          <button
              class="btn btn-primary"
              @click="signAgreement"
              :disabled="processing"
          >
            <span v-if="processing" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Sign and Complete Registration
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { ref, nextTick } from "vue";
import { useAuth } from "@/stores/auth";
import Dialog from "@/components/modals/Dialog.vue";
import PreviewDocumentComponent from "@/views/pages/opportunities/components/PreviewDocumentComponent.vue";
import DocumentsService from "@/services/DocumentsService";

const props = defineProps({
  userData: {
    type: Object,
    required: true
  }
});

const emit = defineEmits(['registration-complete', 'registration-cancelled', 'modal-closed', 'registration-error']);

const auth = useAuth();
const modal = ref(null);
const previewDocumentComponent = ref(null);

const loading = ref(false);
const processing = ref(false);
const loadingMessage = ref('');
const error = ref(null);
const agreementData = ref(null);

const showModal = async () => {
  // Show modal immediately with loader
  modal.value.show();
  
  loading.value = true;
  processing.value = false;
  loadingMessage.value = 'Generating registration agreement...';
  error.value = null;
  agreementData.value = null;
  
  try {
    // Generate the agreement (validation already done in LoginModal)
    agreementData.value = await auth.generateRegistrationAgreement(props.userData);
    
    // Load PDF in preview component using same method as buy form
    await nextTick(); // Ensure component is rendered
    if (previewDocumentComponent.value) {
      await previewDocumentComponent.value.loadPDF(
        DocumentsService.getDocumentPreviewUrl(agreementData.value.agreement.uuid)
      );
    } else {
      console.error('PreviewDocumentComponent not available');
      error.value = 'PDF preview component not available. Please try again.';
      loading.value = false;
      return;
    }
    
    loading.value = false;
  } catch (e) {
    loading.value = false;
    
    // Handle validation errors from backend
    if (e.response && e.response.data && e.response.data.errors) {
      const backendErrors = e.response.data.errors;
      const errorMessages = Object.values(backendErrors).flat();
      error.value = errorMessages.join(', ');
    } else if (e.response && e.response.data && e.response.data.message) {
      error.value = e.response.data.message;
    } else {
      error.value = 'Failed to generate registration agreement. Please try again.';
    }
    
    console.error('Registration agreement generation error:', e);
    
    // Close modal on error and emit error to parent
    hideModal();
    emit('registration-error', error.value);
  }
};

const hideModal = () => {
  if (modal.value) {
    modal.value.hide();
  }
  // Reset state on close so next open is clean
  loading.value = false;
  processing.value = false;
  error.value = null;
  agreementData.value = null;
  // Also close the login modal when agreement modal is closed
  emit('modal-closed');
};

const signAgreement = async () => {
  if (!agreementData.value) return;
  
  processing.value = true;
  
  try {
    const response = await auth.completeRegistration(
      agreementData.value.user.id, 
      agreementData.value.agreement.uuid
    );
    
    hideModal();
    
    // Check if email verification was sent
    if (response.email_verification_sent) {
      emit('registration-complete', {
        ...response,
        requiresEmailVerification: true,
        message: response.message
      });
    } else {
      emit('registration-complete', response);
    }
  } catch (e) {
    processing.value = false;
    error.value = 'Failed to complete registration. Please try again.';
    console.error('Registration completion error:', e);
  }
};

const declineAgreement = () => {
  hideModal();
  emit('registration-cancelled');
};

const onCloseClick = () => {
  if (processing.value) {
    return;
  }
  hideModal();
};

const cancelGeneration = () => {
  // Allow user to cancel while loading
  loading.value = false;
  error.value = null;
  hideModal();
  emit('registration-cancelled');
};

// Dialog component handles modal initialization

// Expose methods
defineExpose({
  showModal,
  hideModal
});
</script>

<style scoped>
.card-footer {
  background: none;
  text-align: center;
}
.pdf-preview {
  border-radius: 8px;
  overflow: hidden;
}

.agreement-info {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.spinner-border.text-electric {
  color: #007bff !important;
  border-top-color: #007bff !important;
}
</style>
